import JSZip from 'jszip'
import {
  SegmentCollection,
  SegmentEntity,
  Track,
  SegmentMaterial,
  RatioTypeUnion,
} from '@ve-pro/types/track.type'
import { useTrackStore, usePlayerStore, useProjectStore } from '@ve-pro/store'
import { Bool, SegmentType } from '@ve-pro/enums/segment'
import {
  AssetType,
  FileExt,
  AssetSource,
  AssetStatus,
  AssetAlpha,
} from '@ve-pro/enums/asset'
import _ from 'lodash'
import { encode } from 'base64-arraybuffer'
import {
  videoEditorRenderer,
  RatioType,
  RatioMap,
  BASE_HEIGHT,
  getTrackLayer,
} from 'video-editor-renderer'
import { transformer } from './Transformer'
import { buffer2Blob } from '@com/utils/blob'
import { assetManager } from '@ve-pro/ve/utils/AssetManager'
import { AsssetsModel } from '@ve-pro/api/aside/type'
import { CommonAssetMap } from '@ve-pro/ve/aside/config/aside-enum'
import { veproLogs } from '@com/utils/report-logs/ve-pro'
import { getProjectInfo } from '@ve-pro/api/project'
import { getFileName } from '@com/utils/file'
import { isSupportMoreRatio } from '@ve-pro/store/tracks/segment/segment-position'
import { handlePreviewFile } from '@ve-pro/ve/aside/config/aside-methods'
import { getGroupBySegment } from '@ve-pro/store/tracks/segment/segment-group'
import { transformSegmentByFragment } from '@ve-pro/store/player/fragment'
import {
  addLangFragmentSegmentIdToFragmentSegmentId,
  getSegmentFragmentById,
} from '@ve-pro/store/tracks/segment/segment-fragment'
import { transformTemplateSegmentDuration } from '@ve-pro/store/player/player-duration'
import { TRACK_SUBTITLES } from '@ve-pro/store/tracks'
import { ProjectTab } from '@ve-pro/enums/project'

/**
 * 指定时间，获取所在时间的分片
 * @param time
 * @returns SegmentCollection
 */
export function getSegmentsByTime(time: number): SegmentCollection[] {
  const trackStore = useTrackStore()

  return trackStore.segments
    .map(addLangFragmentSegmentIdToFragmentSegmentId)
    .map((t) => {
      const group = getGroupBySegment(t)
      const fragment_segment_id = group
        ? group.fragment_segment_id
        : t.fragment_segment_id
      return transformSegmentByFragment(
        t,
        getSegmentFragmentById(fragment_segment_id)!,
        trackStore.isEnterSegmentFragmentEditMode,
        trackStore.activeSegmentFragment?.id,
      ) as SegmentCollection
    })
    .filter((t) => t !== null)
    .map((t) => transformTemplateSegmentDuration(t))
    .filter((segment) => {
      return isWithIn(segment, time)
    })
}

/**
 * 判断是否是实体分片
 * @param segment
 */
export function isEntitySegment(
  segment: SegmentCollection,
): segment is SegmentEntity {
  return [SegmentType.Sticker, SegmentType.Text, SegmentType.Video].includes(
    segment.type,
  )
}

/**
 * 判断分片是否处于指定时间内
 * @param segment
 * @param time
 * @returns
 */
export function isWithIn(segment: SegmentCollection, time: number): boolean {
  return (
    time >= segment.start_time && time < segment.start_time + segment.duration
  )
}

/**
 * 分贝转音量
 * @param decibel 分贝
 * @returns 音量
 */
export function decibelsToGain(decibel: number): number {
  return Math.exp(decibel / 8.6858)
}

/**
 * 返回轨道索引
 * @param track_id
 * @returns
 */
export function getTrackIndex(track_id: Track['id']): number {
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()
  const useTemplateTracks = projectStore.tab !== ProjectTab.Creative

  const track = trackStore.tracks.find((t) => t.id === track_id)
  if (!track) {
    return 0
  }

  return getTrackLayer(
    track,
    [
      ...trackStore.defaultExcludeFragmentTracks.filter(
        (t) => t.id !== TRACK_SUBTITLES,
      ),
      ...trackStore.subtitleTracks,
    ],
    trackStore.fragmentTracks,
    useTemplateTracks ? trackStore.templateTracks : [],
  )
}

export function toBase64(arrayBuffer: ArrayBuffer, type: FileExt): string {
  switch (type) {
    case FileExt.MP3:
      return `data:audio/mpeg;base64,${encode(arrayBuffer)}`
    case FileExt.GIF:
      return `data:image/gif;base64,${encode(arrayBuffer)}`
    default:
      return ''
  }
}

/**
 * 判读资源直实类型
 * @param segment
 * @param type
 * @returns
 */
export function checkFileExt(
  segment: SegmentCollection,
  type: FileExt,
): boolean {
  if (segment.type === SegmentType.Text) return false

  return segment.material.file_ext === type
}

export const DEFAULT_RATIO = RatioType['9:16']

// 更新素材大小，铺满画布
export function updateSegmentToContain(
  segment: SegmentCollection,
): SegmentCollection {
  const playerStore = usePlayerStore()

  const canvas = document.getElementById('unity-canvas') as HTMLDivElement
  const width = canvas.offsetWidth
  const height = canvas.offsetHeight

  // 素材宽高不大于画布时
  if (
    segment.type === SegmentType.Sticker &&
    segment.material.width < width / playerStore.ratio &&
    segment.material.height < height / playerStore.ratio
  ) {
    segment.width = segment.material.width
    segment.height = segment.material.height
    return segment
  }

  switch (segment.type) {
    case SegmentType.Video:
    case SegmentType.Sticker: {
      const scale = Math.min(
        width / segment.material.width,
        height / segment.material.height,
      )

      segment.width = Math.floor(
        (segment.material.width / playerStore.ratio) *
          scale *
          playerStore.scale,
      )
      segment.height = Math.floor(
        (segment.material.height / playerStore.ratio) *
          scale *
          playerStore.scale,
      )
      break
    }
    case SegmentType.Text: {
      // const scale = Math.min(width / segment.width, height / segment.height)

      // segment.scale_x = (segment.scale_x / playerStore.ratio) * scale
      // segment.scale_y = (segment.scale_y / playerStore.ratio) * scale

      break
    }
    default:
      break
  }

  return segment
}

function updateSegmentRatioToContain(
  segment: SegmentCollection,
  ratio: RatioTypeUnion,
  relativeTo?: SegmentCollection,
): SegmentCollection {
  if (isSupportMoreRatio(segment) && segment.ratios && segment.ratios[ratio]) {
    const baseHeight = BASE_HEIGHT

    let canvasWidth = RatioMap[ratio][0]
    let canvasHeight = RatioMap[ratio][1]

    let baseScale = baseHeight / canvasHeight

    if (relativeTo && isSupportMoreRatio(relativeTo)) {
      baseScale = 1

      canvasWidth = relativeTo.ratios?.[ratio]!.width ?? RatioMap[ratio][0]
      canvasHeight = relativeTo.ratios?.[ratio]!.height ?? RatioMap[ratio][1]
    }

    // 素材宽高不大于画布时
    if (
      segment.type === SegmentType.Sticker &&
      segment.material.width < canvasWidth &&
      segment.material.height < canvasHeight
    ) {
      segment.ratios[ratio]!.width = segment.material.width
      segment.ratios[ratio]!.height = segment.material.height

      return segment
    }

    if (
      segment.type === SegmentType.Sticker ||
      segment.type === SegmentType.Video
    ) {
      const scale = Math.min(
        canvasWidth / segment.material.width,
        canvasHeight / segment.material.height,
      )

      segment.ratios[ratio]!.width = Math.floor(
        segment.material.width * scale * baseScale,
      )
      segment.ratios[ratio]!.height = Math.floor(
        segment.material.height * scale * baseScale,
      )
    }
  }

  return segment
}

// 更新素材大小，铺满相应尺寸的画布
export function updateSegmentRatiosToContain(
  segment: SegmentCollection,
  relativeTo?: SegmentCollection,
): SegmentCollection {
  const projectStore = useProjectStore()
  if (isSupportMoreRatio(segment)) {
    projectStore.ratios.forEach((ratio) => {
      updateSegmentRatioToContain(segment, ratio, relativeTo)
    })
  }

  return segment
}

/* 光标处插入html代码，参数是String类型的html代码，例子："<p>猪头诺</p>"*/
export const insertHtml = (html: string): void => {
  let sel, range
  if (window.getSelection) {
    sel = window.getSelection()
    if (sel && sel.getRangeAt && sel.rangeCount) {
      range = sel.getRangeAt(0)
      range.deleteContents()
      // Range.createContextualFragment() would be useful here but is
      // non-standard and not supported in all browsers (IE9, for one)
      const el = document.createElement('div')
      el.innerHTML = html
      const frag = document.createDocumentFragment()
      let node
      let lastNode
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node)
      }
      range.insertNode(frag)
      // Preserve the selection
      if (lastNode) {
        range = range.cloneRange()
        range.setStartAfter(lastNode)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }
  }
}

// 切换transformer的聚焦状态
export function changeTransformerFocusByTime(
  ids: number[] | null,
  time: number,
): void {
  if (ids === null) {
    transformer.unfocus()

    return
  }

  const visibleSegments = getSegmentsByTime(time).filter((segment) =>
    ids.includes(segment.id),
  )
  console.log('visibleSegments', visibleSegments)

  if (visibleSegments.length) {
    transformer.focus(visibleSegments)
    visibleSegments.forEach((segment) => {
      transformer.toggleNode(segment, true)
    })
  } else {
    transformer.unfocus()
    visibleSegments.forEach((segment) => {
      transformer.toggleNode(segment, false)
    })
  }
}

export async function getAssetUrl(
  material: SegmentMaterial,
  options?: {
    logAction?: ReturnType<typeof veproLogs.createAction>
  },
): Promise<string> {
  switch (material.type) {
    case AssetType.Video: {
      // return buffer2Blob(await assetManager.get(material.out_mp4_url))
      const mp4Url = await assetManager.getUrlByAsset(
        material,
        'out_mp4_preview_url',
        {
          enableLog: Boolean(options?.logAction),
          logAction: options?.logAction,
          asset: material,
        },
      )
      return buffer2Blob(
        await assetManager.get(mp4Url, undefined, {
          enableLog: Boolean(options?.logAction),
          logAction: options?.logAction,
          asset: material,
        }),
      )
    }
    case AssetType.Filter:
    case AssetType.Transition:
      return buffer2Blob(
        await assetManager.get(material.file_urls![0], material.file_md5, {
          enableLog: Boolean(options?.logAction),
          logAction: options?.logAction,
          asset: material,
        }),
      )
    case AssetType.Animation: {
      if (material.file_urls === null || material.file_urls.length === 0) {
        return buffer2Blob(
          await assetManager.get(material.file_url, material.file_md5, {
            enableLog: Boolean(options?.logAction),
            logAction: options?.logAction,
            asset: material,
          }),
        )
      }

      const urls = material.file_urls!.map(async (url) =>
        buffer2Blob(
          await assetManager.get(
            url,
            `${material.file_md5}_${getFileName(url, true)}`,
            {
              enableLog: Boolean(options?.logAction),
              logAction: options?.logAction,
              asset: material,
            },
          ),
        ),
      )
      return (await Promise.all(urls)).join(',')
    }
    default:
      return buffer2Blob(await assetManager.get(material.file_url))
  }
}

/**
 * 把公共资源库的数据转化为项目数据，
 * 用于预览，补充关键字段
 * @param asset
 * @returns
 */
export function commonAsset2ProjectAsset(asset: AsssetsModel): SegmentMaterial {
  return {
    adv_id: 0,
    created_time: 0,
    file_ext: asset.format,
    file_size: 0,
    file_name: asset.name,
    file_url: asset.file_url,
    file_urls: asset.file_urls,
    file_md5: asset.file_md5,
    out_mp4_url: asset.format === FileExt.MP4 ? asset.file_url : '',
    out_mp4_preview_url: asset.format === FileExt.MP4 ? asset.file_url : '',
    out_mp3_url: '',
    height: asset.height,
    id: asset.id,
    asset_id: asset.id,
    mime_type: '',
    type: CommonAssetMap[asset.type as number] as AssetType,
    duration: asset.duration,
    width: asset.width,
    status: AssetStatus.Active,
    thumb_url: asset.thumb_url,
    name: asset.name,
    ratio: '',
    updated_time: 0,
    progress: 0,
    source: AssetSource.Public,
    source_id: asset.id,
    is_default: Bool.Truly,
    text: '',
    frame_rate: 0,
    shot_status: null,
    shot_frame_ids: '',
    alpha: AssetAlpha.No,
  }
}

// 通过宽高、码率、帧率和时长预估mp4的大小
export function estimateMp4Size(bitrate: number, duration: number): number {
  const size = (duration * bitrate) / 8
  return Math.ceil(size)
}

export function getUniqueId(segment: SegmentCollection): string {
  return `${segment.track_id}_${segment.id}`
}

export async function unzip(url: string): Promise<string[]> {
  try {
    const zip = new JSZip()

    const arrayBuffer = await assetManager.get(url)

    const res = await zip.loadAsync(arrayBuffer)

    const files = Object.keys(res.files)

    const fileNamesList = files.filter((fileName) => {
      return !fileName.startsWith('__MACOSX') && !zip.files[fileName]?.dir
    })

    const data = await Promise.all(
      fileNamesList.map((fileName) => res.file(fileName)!.async('blob')),
    )

    return data.map((t) => URL.createObjectURL(t))
  } catch (err) {
    return Promise.reject(err)
  }
}

// 下载项目数据为zip包，用于引擎研发获取调试数据
export async function downloadProjectData(): Promise<void> {
  const projectStore = useProjectStore()

  const res = await getProjectInfo({ id: projectStore.id })

  const data = _.get(res, 'data')
  console.log(data)
  await videoEditorRenderer.parseInfoData(data)
}

export function resolveVePublicPath(): string {
  return process.env.PUBLIC_PATH === ''
    ? process.env.PUBLIC_PATH
    : `${process.env.PUBLIC_PATH}ve/`
}

export function videoToAsset(url: string, ratio: RatioType): SegmentMaterial {
  const [width, height] = RatioMap[ratio]

  return {
    adv_id: 0,
    created_time: 0,
    file_ext: FileExt.MP4,
    file_size: 0,
    file_name: url,
    file_url: url,
    file_urls: null,
    file_md5: '',
    out_mp4_url: url,
    out_mp4_preview_url: url,
    out_mp3_url: '',
    height: height,
    id: 0,
    asset_id: 0,
    mime_type: '',
    type: AssetType.Video,
    duration: 0,
    width: width,
    status: AssetStatus.Active,
    thumb_url: url,
    name: 'mp4',
    ratio: '',
    updated_time: 0,
    progress: 0,
    source: AssetSource.Public,
    source_id: 0,
    is_default: Bool.Truly,
    text: '',
    frame_rate: 0,
    shot_status: null,
    shot_frame_ids: '',
    alpha: AssetAlpha.No,
  }
}

export function previewVideo(
  url: string,
  ratio: RatioType,
  duration: number,
  options?: {
    assetId?: number
  },
): number {
  const assetId = options?.assetId ?? Date.now()
  const asset = videoToAsset(url, ratio)

  asset.duration = duration
  asset.asset_id = assetId
  asset.id = assetId

  handlePreviewFile(asset)

  return assetId
}
